import React, { useState } from "react";
import {
  DataGridPremium,
  GridColDef,
  GridRowModel,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid-premium";
import { Box, Tooltip, Button, Stack, IconButton, Chip } from "@mui/material";
import FlexBetween from "../FlexBetween";

import { IconDownloadSvg } from "../../common/utils/icons";
import ValidationErrorDataGrid from "./ValidationErrorDataGrid";
import { disableRowGroupByColumnProps } from "../../components/DataGridProps/DataGridProps";
import Loader from "../Molecules/Loader/Loader";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import DataGridPaginationFooter from "./DataGridPaginationFooter";

interface DataTableProps {
  dataRows: any[];
  dataColumns: GridColDef[];
  loading?: boolean;
  checkboxSelection?: boolean;
  className?: string;
  buttonText?: string;
  buttonClass?: string;
  buttonClick?: () => void;
  buttonIcon?: React.ReactNode;
  dataListTitle?: string;
  isExportButtonRequired?: boolean;
  rowCount?: number;
  pageSizeOptions?: number[];
  paginationModel?: any;
  onPaginationModelChange?: (params: any) => void;
  historyType: string;
  downloadFromAPIFile?: (params: any) => void;
  setLoading?: (params: any) => any;
  disableColumnFilter?: boolean;
  rowGroupByColumnProps?: any;
  validationErrorProps?: any;
}

const ValidationSummaryDataGrid: React.FC<DataTableProps> = ({
  dataRows,
  dataColumns,
  checkboxSelection = false,
  loading,
  className,
  buttonText,
  buttonClass,
  buttonClick,
  buttonIcon,
  dataListTitle,
  isExportButtonRequired,
  rowCount,
  pageSizeOptions,
  paginationModel,
  onPaginationModelChange,
  historyType,
  downloadFromAPIFile,
  setLoading,
  disableColumnFilter,
  rowGroupByColumnProps = { ...disableRowGroupByColumnProps },
  validationErrorProps: {
    dataRows: validationDataRows,
    dataColumns: ValidationDataColumns,
    loading: ValidationIsLoading,
    className: ValidationClassName,
    gridColumnLength: ValidationGridColumnLength,
    dataListTitle: ValidationDataListTitle,
    isHeight100: ValidationIsHeight100,
    pinnedColumns,
    handleMoveColumns,
    isAllColumnPinned,
    disableColumnPinning,
    validationCheckboxSelection,
    selectedIds,
    setSelectedIds,
    validationRowCount,
    validationPageSizeOptions,
    validationPaginationModel,
    validationOnPaginationModelChange,
    validationPaginationMode = "client",
    isIssuesDataAvailable = true,
  },
}) => {
  const { rowsPerPage, rowsPerPageOptions } = useRowsPerPage();
  const [expandedRowId, setExpandedRowId] = useState<any>(null);

  function CustomToolbar() {
    return (
      <GridToolbarContainer className="custom-toolbar">
        <Stack
          spacing={1}
          direction="row"
          alignItems="center"
          justifyContent={dataListTitle ? "space-between" : "flex-end"}
          sx={{ width: "100%", marginRight: 3, marginLeft: 3 }}
        >
          {dataListTitle && <h2 className="list-title">{dataListTitle}</h2>}
          <Box sx={{ display: "flex", columnGap: "10px" }}>
            {buttonClick && (
              <Button
                color="secondary"
                variant="contained"
                onClick={buttonClick}
                className={buttonClass}
              >
                <span
                  style={{
                    marginRight: "4px",
                    position: "relative",
                    top: "2px",
                  }}
                >
                  {buttonIcon}
                </span>{" "}
                {buttonText}
              </Button>
            )}
            {downloadFromAPIFile && (
              <Tooltip title="Download Excel" placement="top">
                <button
                  className="btn-export-1 btn-orange btn-border"
                  onClick={() => downloadFromAPIFile(setLoading)}
                >
                  <IconDownloadSvg />
                </button>
              </Tooltip>
            )}
            {isExportButtonRequired && !downloadFromAPIFile && (
              <GridToolbarExport
                className="btn-orange btn-sm btn-border btn-export"
                excelOptions={{
                  fields: dataColumns
                    .filter(
                      (col) =>
                        col.field !== "action" &&
                        col.field !== "actions" &&
                        col.field !== "__detail_panel_toggle__" &&
                        col.field !== "checkbox" &&
                        col.field !== "srNo" &&
                        col.field !== "id",
                    )
                    .map((col) => col.field),
                }}
                printOptions={{ disableToolbarButton: true }}
                csvOptions={{ disableToolbarButton: true }}
              />
            )}
          </Box>
        </Stack>
      </GridToolbarContainer>
    );
  }

  return (
    <>
      <FlexBetween height="100%" width="100%">
        <div style={{ width: "100%" }}>
          <DataGridPremium
            getRowHeight={() => "auto"}
            className={className}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#146FD0",
                fontWeight: 700,
              },
              minHeight: "100px",
              // disable sort arrow in header
              "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer":
                {},
              "& .MuiDataGrid-sortIcon": {},
              "& .MuiDataGrid-virtualScroller": {
                maxHeight: "calc(100vh - 230px)",
                //minHeight: "230px",
                overflowY: "auto !important",
                //overflowX: "hidden",
              },
              "& .MuiDataGrid-row .MuiIconButton-sizeSmall": {
                transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
              },
              "& .MuiDataGrid-row.MuiDataGrid-row--detailPanelExpanded .MuiIconButton-sizeSmall":
                {
                  transform: "rotateZ(180deg)",
                },
            }}
            rows={dataRows || []}
            columns={dataColumns}
            paginationMode="server"
            checkboxSelection={checkboxSelection}
            loading={loading}
            pagination={true}
            getRowId={(row: GridRowModel) => row?.id}
            autoHeight={true}
            disableColumnFilter={disableColumnFilter}
            slots={{
              toolbar: CustomToolbar,
              footer: () => (
                <DataGridPaginationFooter pageSizeOptions={pageSizeOptions} />
              ),
            }}
            getRowClassName={(params: any) => {
              return params?.row?.is_derived ? "derived-column" : "d-none";
            }}
            getDetailPanelHeight={(params: any) => {
              const isCustomValidation =
                params?.row?.custom_validations?.expressions[0]?.length > 0;
              return params?.row?.is_derived || isCustomValidation
                ? "auto"
                : "auto";
            }}
            detailPanelExpandedRowIds={
              expandedRowId !== null ? [expandedRowId] : []
            }
            onDetailPanelExpandedRowIdsChange={(ids) => {
              setExpandedRowId(ids.length > 0 ? ids[ids.length - 1] : null);
            }}
            getDetailPanelContent={() => {
              /** Derive type per record: a populated custom_validation_name → Custom; otherwise → General. */
              const deriveType = (r: any): "Custom" | "General" => {
                const name = r?.custom_validation_name;
                return typeof name === "string" && name.trim().length > 0
                  ? "Custom"
                  : "General";
              };

              const taggedRows = (validationDataRows || []).map((r: any) => ({
                ...r,
                validation_type: deriveType(r),
              }));

              const typeChipStyle = (t: string) => {
                switch (t) {
                  case "Custom":
                    return { bg: "#FFF3E0", color: "#E65100" };
                  default:
                    return { bg: "#E3F2FD", color: "#0D47A1" };
                }
              };

              const validationTypeColumn: GridColDef = {
                field: "validation_type",
                headerName: "Validation Type",
                width: 160,
                minWidth: 130,
                sortable: true,
                filterable: true,
                renderCell: (p: any) => {
                  const t = p?.value || "";
                  const c = typeChipStyle(t);
                  return (
                    <Chip
                      label={t}
                      size="small"
                      sx={{
                        backgroundColor: c.bg,
                        color: c.color,
                        fontWeight: 600,
                        border: `1px solid ${c.color}33`,
                      }}
                    />
                  );
                },
              };

              const unifiedColumns: GridColDef[] = [
                validationTypeColumn,
                ...((ValidationDataColumns as GridColDef[]) || []),
              ];

              return (
                <Box className="rule-execution rule-execution-history italic-none">
                  <Box className="position-relative" sx={{ p: 2 }}>
                    {isIssuesDataAvailable && (
                      <IconButton
                        onClick={handleMoveColumns}
                        className={`icon-pin-datagrid navbar-icon burger  ${
                          isAllColumnPinned ? "active" : ""
                        }`}
                      >
                        <div className="bar bar1"></div>
                        <div className="bar bar2"></div>
                        <div className="bar bar3"></div>
                      </IconButton>
                    )}
                    <ValidationErrorDataGrid
                      dataRows={taggedRows}
                      dataColumns={unifiedColumns}
                      loading={ValidationIsLoading}
                      className={ValidationClassName}
                      dataListTitle={ValidationDataListTitle}
                      gridColumnLength={(ValidationGridColumnLength || 0) + 1}
                      isHeight100={ValidationIsHeight100}
                      pinnedColumns={
                        validationDataRows?.length > 0 ? pinnedColumns : {}
                      }
                      disableColumnPinning={disableColumnPinning}
                      validationCheckboxSelection={validationCheckboxSelection}
                      selectedIds={selectedIds}
                      setSelectedIds={setSelectedIds}
                      paginationMode="server"
                      rowCount={validationRowCount}
                      pageSizeOptions={
                        validationPageSizeOptions || pageSizeOptions
                      }
                      paginationModel={validationPaginationModel}
                      onPaginationModelChange={
                        validationOnPaginationModelChange
                      }
                    />
                  </Box>
                </Box>
              );
            }}
            //for server side pagination
            rowCount={rowCount || 0}
            pageSizeOptions={pageSizeOptions || rowsPerPageOptions}
            paginationModel={
              paginationModel ?? { page: 0, pageSize: rowsPerPage }
            }
            onPaginationModelChange={onPaginationModelChange}
            {...rowGroupByColumnProps}
            localeText={{ noRowsLabel: "Data not available" }}
          />
        </div>
      </FlexBetween>
    </>
  );
};

export default ValidationSummaryDataGrid;
